import { useState, FormEvent } from 'react';
import { useCart } from '../../contexts/CartContext';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CheckoutModal = ({ isOpen, onClose }: CheckoutModalProps) => {
  const { items, totalPrice, clearCart } = useCart();
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'paypal'>('card');
  const [isProcessingPayPal, setIsProcessingPayPal] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    billingAddress: '',
    cardNumber: '',
    expiry: '',
    cvv: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateBasicInfo = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s\-()]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number';
    }

    if (!formData.billingAddress.trim()) {
      newErrors.billingAddress = 'Billing address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = () => {
    const basicValid = validateBasicInfo();
    
    if (paymentMethod === 'card') {
      const cardErrors: Record<string, string> = {};

      if (!formData.cardNumber.trim()) {
        cardErrors.cardNumber = 'Card number is required';
      } else if (!/^\d{16}$/.test(formData.cardNumber.replace(/\s/g, ''))) {
        cardErrors.cardNumber = 'Card number must be 16 digits';
      }

      if (!formData.expiry.trim()) {
        cardErrors.expiry = 'Expiry date is required';
      } else if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(formData.expiry)) {
        cardErrors.expiry = 'Format: MM/YY';
      }

      if (!formData.cvv.trim()) {
        cardErrors.cvv = 'CVV is required';
      } else if (!/^\d{3,4}$/.test(formData.cvv)) {
        cardErrors.cvv = 'CVV must be 3-4 digits';
      }

      setErrors(prev => ({ ...prev, ...cardErrors }));
      return basicValid && Object.keys(cardErrors).length === 0;
    }

    return basicValid;
  };

  const handlePayPalPayment = async () => {
    if (!validateBasicInfo()) return;

    setIsProcessingPayPal(true);
    
    try {
      // Simulate PayPal payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const generatedOrderNumber = `AZZ-PP-${Date.now().toString().slice(-8)}`;
      setOrderNumber(generatedOrderNumber);
      setShowSuccess(true);
    } catch (error) {
      console.error('PayPal payment failed:', error);
    } finally {
      setIsProcessingPayPal(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const generatedOrderNumber = `AZZ-${Date.now().toString().slice(-8)}`;
      setOrderNumber(generatedOrderNumber);
      setShowSuccess(true);
    }
  };

  const handleClose = () => {
    if (showSuccess) {
      clearCart();
      setShowSuccess(false);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        billingAddress: '',
        cardNumber: '',
        expiry: '',
        cvv: ''
      });
      setErrors({});
      setPaymentMethod('card');
    }
    onClose();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const chunks = cleaned.match(/.{1,4}/g);
    return chunks ? chunks.join(' ') : cleaned;
  };

  const formatExpiry = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
    }
    return cleaned;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose}></div>
      
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {!showSuccess ? (
          <>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between z-10">
              <h2 className="text-3xl font-bold text-slate-900">Checkout</h2>
              <button
                onClick={handleClose}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-6">Payment Information</h3>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Basic Information */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => handleInputChange('fullName', e.target.value)}
                      className={`w-full px-4 py-3 border ${errors.fullName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm`}
                      placeholder="John Doe"
                    />
                    {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm`}
                      placeholder="john@example.com"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={`w-full px-4 py-3 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm`}
                      placeholder="+1 (555) 123-4567"
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">
                      Billing Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.billingAddress}
                      onChange={(e) => handleInputChange('billingAddress', e.target.value)}
                      rows={3}
                      className={`w-full px-4 py-3 border ${errors.billingAddress ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm resize-none`}
                      placeholder="123 Main St, Austin, TX 78701"
                    />
                    {errors.billingAddress && <p className="text-red-500 text-xs mt-1">{errors.billingAddress}</p>}
                  </div>

                  {/* Payment Method Selection */}
                  <div className="pt-4 border-t border-gray-200">
                    <h4 className="text-base font-bold text-slate-900 mb-4">Payment Method</h4>
                    
                    <div className="grid grid-cols-2 gap-3 mb-6">
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('card')}
                        className={`p-4 border-2 rounded-lg flex items-center justify-center gap-3 transition-all cursor-pointer ${
                          paymentMethod === 'card'
                            ? 'border-violet-500 bg-violet-50 text-violet-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <i className="ri-bank-card-line text-xl"></i>
                        <span className="font-semibold">Credit Card</span>
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('paypal')}
                        className={`p-4 border-2 rounded-lg flex items-center justify-center gap-3 transition-all cursor-pointer ${
                          paymentMethod === 'paypal'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <i className="ri-paypal-line text-xl"></i>
                        <span className="font-semibold">PayPal</span>
                      </button>
                    </div>

                    {/* Card Payment Form */}
                    {paymentMethod === 'card' && (
                      <div className="space-y-4">
                        {/* Accepted Card Logos */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-gray-500 font-medium mr-1">Accepted Cards:</span>
                          {[
                            { src: 'https://cdn.jsdelivr.net/npm/payment-icons@1.1.0/min/flat/visa.svg', alt: 'Visa' },
                            { src: 'https://cdn.jsdelivr.net/npm/payment-icons@1.1.0/min/flat/mastercard.svg', alt: 'Mastercard' },
                            { src: 'https://cdn.jsdelivr.net/npm/payment-icons@1.1.0/min/flat/amex.svg', alt: 'American Express' },
                            { src: 'https://cdn.jsdelivr.net/npm/payment-icons@1.1.0/min/flat/discover.svg', alt: 'Discover' },
                          ].map((card) => (
                            <div
                              key={card.alt}
                              className="w-11 h-7 border border-gray-200 bg-white rounded flex items-center justify-center p-1"
                              title={card.alt}
                            >
                              <img src={card.src} alt={card.alt} className="w-full h-full object-contain" />
                            </div>
                          ))}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-slate-900 mb-2">
                            Card Number <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={formData.cardNumber}
                              onChange={(e) => {
                                const formatted = formatCardNumber(e.target.value.replace(/\s/g, '').slice(0, 16));
                                handleInputChange('cardNumber', formatted);
                              }}
                              className={`w-full px-4 py-3 border ${errors.cardNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm`}
                              placeholder="1234 5678 9012 3456"
                              maxLength={19}
                            />
                            <i className="ri-bank-card-line absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl"></i>
                          </div>
                          {errors.cardNumber && <p className="text-red-500 text-xs mt-1">{errors.cardNumber}</p>}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-slate-900 mb-2">
                              Expiry Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.expiry}
                              onChange={(e) => {
                                const formatted = formatExpiry(e.target.value);
                                handleInputChange('expiry', formatted);
                              }}
                              className={`w-full px-4 py-3 border ${errors.expiry ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm`}
                              placeholder="MM/YY"
                              maxLength={5}
                            />
                            {errors.expiry && <p className="text-red-500 text-xs mt-1">{errors.expiry}</p>}
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-slate-900 mb-2">
                              CVV <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.cvv}
                              onChange={(e) => handleInputChange('cvv', e.target.value.replace(/\D/g, '').slice(0, 4))}
                              className={`w-full px-4 py-3 border ${errors.cvv ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm`}
                              placeholder="123"
                              maxLength={4}
                            />
                            {errors.cvv && <p className="text-red-500 text-xs mt-1">{errors.cvv}</p>}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* PayPal Payment */}
                    {paymentMethod === 'paypal' && (
                      <div className="bg-blue-50 rounded-lg p-6 text-center">
                        <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                          <i className="ri-paypal-fill text-blue-600 text-3xl"></i>
                        </div>
                        <h4 className="text-lg font-semibold text-blue-900 mb-2">PayPal Payment</h4>
                        <p className="text-sm text-blue-700 mb-6">
                          You will be redirected to PayPal to complete your payment securely.
                        </p>
                        <button
                          type="button"
                          onClick={handlePayPalPayment}
                          disabled={isProcessingPayPal}
                          className="w-full bg-blue-600 text-white py-4 rounded-lg font-bold text-base hover:bg-blue-700 transition-all shadow-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isProcessingPayPal ? (
                            <div className="flex items-center justify-center gap-3">
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              Processing...
                            </div>
                          ) : (
                            <>Pay with PayPal - ${totalPrice}</>
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Card Payment Button */}
                  {paymentMethod === 'card' && (
                    <button
                      type="submit"
                      className="w-full bg-gradient-to-r from-violet-500 to-purple-600 text-white py-4 rounded-lg font-bold text-base hover:from-violet-600 hover:to-purple-700 transition-all shadow-lg whitespace-nowrap cursor-pointer mt-6"
                    >
                      Place Order - ${totalPrice}
                    </button>
                  )}
                </form>
              </div>

              <div>
                <div className="bg-gray-50 rounded-xl p-6 sticky top-24">
                  <h3 className="text-xl font-bold text-slate-900 mb-6">Order Summary</h3>
                  
                  <div className="space-y-4 mb-6">
                    {items.map((item) => (
                      <div key={item.id} className="flex items-start space-x-4">
                        <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-br ${item.color} rounded-lg flex-shrink-0`}>
                          <i className={`${item.icon} text-white text-xl`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-slate-900 truncate">{item.name}</h4>
                          <p className="text-sm text-gray-600">${item.price}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-200 pt-4 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Subtotal ({items.length} {items.length === 1 ? 'service' : 'services'})</span>
                      <span className="font-semibold text-slate-900">${totalPrice}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Setup Fee</span>
                      <span className="font-semibold text-green-600">FREE</span>
                    </div>
                    <div className="border-t border-gray-200 pt-3 flex items-center justify-between">
                      <span className="text-base font-bold text-slate-900">Total</span>
                      <span className="text-2xl font-bold text-violet-600">${totalPrice}</span>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-violet-50 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <i className="ri-shield-check-line text-violet-600 text-xl flex-shrink-0 mt-0.5"></i>
                      <div>
                        <p className="text-xs font-semibold text-violet-900 mb-1">Secure Payment</p>
                        <p className="text-xs text-violet-700">Your payment information is encrypted and secure</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="p-12 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
              <i className="ri-check-line text-green-600 text-5xl"></i>
            </div>
            
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Order Confirmed!</h2>
            <p className="text-base text-gray-600 mb-8">
              Thank you for your purchase. Your order has been successfully placed.
            </p>

            <div className="bg-gray-50 rounded-xl p-6 max-w-md mx-auto mb-8">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <span className="text-sm font-semibold text-gray-600">Order Number</span>
                <span className="text-sm font-bold text-slate-900">{orderNumber}</span>
              </div>
              
              <div className="space-y-3 mb-4">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{item.name}</span>
                    <span className="text-sm font-semibold text-slate-900">${item.price}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-4 flex items-center justify-between">
                <span className="text-base font-bold text-slate-900">Total</span>
                <span className="text-xl font-bold text-violet-600">${totalPrice}</span>
              </div>
            </div>

            <div className="bg-violet-50 rounded-xl p-6 max-w-md mx-auto mb-8">
              <p className="text-sm text-violet-900 mb-2">
                <strong>What's Next?</strong>
              </p>
              <p className="text-sm text-violet-700">
                Our team will contact you within 24 hours at <strong>{formData.email}</strong> to schedule your onboarding and service setup.
              </p>
            </div>

            <button
              onClick={handleClose}
              className="bg-gradient-to-r from-violet-500 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-violet-600 hover:to-purple-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutModal;